import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/apiClient';
import { IconAlertTriangle, IconCheck, IconClock, IconTrash } from '../ui/icons';
import { moduleColor } from '../ui/modulePalette';

function formatDateTime(input) {
  const raw = String(input || '').trim();
  if (!raw) return '—';
  const d = new Date(raw.replace(' ', 'T'));
  if (!Number.isFinite(d.getTime())) return raw;
  try {
    return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'full', timeStyle: 'short' }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

function contactTitle(c) {
  const company = String(c?.company_name || '').trim();
  const name = `${String(c?.first_name || '').trim()} ${String(c?.last_name || '').trim()}`.trim();
  return company || name || `Contact #${c?.id ?? ''}`;
}

function nowDateTimeLocal() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = now.getFullYear();
  const mm = pad(now.getMonth() + 1);
  const dd = pad(now.getDate());
  const hh = pad(now.getHours());
  const min = pad(now.getMinutes());
  return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${min}` };
}

export default function PublicAppointmentPage({ token, embedded = false, onChanged }) {
  const t = String(token || '').trim();
  const accent = moduleColor('appointments');
  const [state, setState] = useState({ status: 'loading', error: '', appointment: null });
  const [form, setForm] = useState(() => ({ ...nowDateTimeLocal() }));
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!t) {
      setState({ status: 'error', error: 'Lien invalide', appointment: null });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading', error: '', appointment: null });
    api
      .appointmentPublicGet(t)
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: 'error', error: res.error || 'Lien invalide', appointment: null });
          return;
        }
        setState({ status: 'ready', error: '', appointment: res.appointment || null });
        setResult(null);
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'error', error: 'Impossible de charger', appointment: null });
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const statusLabel = useMemo(() => {
    const s = String(state.appointment?.status || 'scheduled');
    if (s === 'cancelled') return 'Annulé';
    if (s === 'postponed') return 'Reporté';
    return 'Planifié';
  }, [state.appointment?.status]);

  async function doAction(action, extra) {
    if (!t) return;
    setSaving(true);
    setResult(null);
    const res = await api.appointmentPublicAction({ token: t, action, ...(extra || {}) });
    if (!res.ok) {
      setSaving(false);
      setResult({ ok: false, message: res.error || 'Erreur' });
      return;
    }
    setState((s) => ({ ...s, appointment: res.appointment || s.appointment }));
    setSaving(false);
    setResult({ ok: true, message: 'Modification enregistrée. Un email de confirmation a été envoyé.' });
    try {
      onChanged?.(res.appointment || null, action);
    } catch {
      // ignore
    }
  }

  const inner = (
      <div style={{ width: 'min(860px, 100%)', display: 'grid', gap: 14, margin: embedded ? '0 auto' : undefined }}>
        <div className="Card">
        <div className="Row" style={{ marginBottom: 10 }}>
          <div>
            <div className="TitleMd">Modifier le rendez-vous</div>
            <div className="Muted">Accès sécurisé via lien unique.</div>
          </div>
          <span className="Badge" style={{ borderColor: accent, color: accent }}>
            <span className="ButtonInline" style={{ gap: 8 }}>
              <IconClock /> {statusLabel}
            </span>
          </span>
        </div>

        {state.status === 'loading' ? <div className="Muted">Chargement…</div> : null}
        {state.status === 'error' ? (
          <div className="Error" role="alert">
            {state.error}
          </div>
        ) : null}

        {state.status === 'ready' && state.appointment ? (
          <div className="Notice" style={{ marginTop: 10 }}>
            <div className="TitleMd" style={{ marginBottom: 6 }}>
              {contactTitle(state.appointment.client)}
            </div>
            <div className="Muted">
              Date/heure actuelle : <strong>{formatDateTime(state.appointment.start_at)}</strong>
              <br />
              Lieu : <strong>{state.appointment.location_label || '—'}</strong>
            </div>
          </div>
        ) : null}
        </div>

      {state.status === 'ready' && state.appointment ? (
        <>
          <div className="Card">
            <div className="Row" style={{ marginBottom: 10 }}>
              <div className="TitleMd">Changer la date</div>
            </div>
            <div className="FormGrid">
              <div className="Field">
                <label className="Label">Date</label>
                <input className="Input" type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} disabled={saving} />
              </div>
              <div className="Field">
                <label className="Label">Heure</label>
                <input className="Input" type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} disabled={saving} />
              </div>
            </div>
            <div className="Badges" style={{ justifyContent: 'flex-start', marginTop: 10 }}>
              <button type="button" className="Button" disabled={saving || !String(form.date || '') || !String(form.time || '')} onClick={() => doAction('reschedule', { date: form.date, time: form.time })}>
                {saving ? 'Enregistrement…' : 'Enregistrer la nouvelle date'}
              </button>
              <button
                type="button"
                className="Button Button--ghost"
                disabled={saving}
                onClick={() => {
                  const dt = nowDateTimeLocal();
                  setForm(dt);
                }}
              >
                Maintenant
              </button>
            </div>
          </div>

          <div className="Card">
            <div className="Row" style={{ marginBottom: 10 }}>
              <div className="TitleMd">Reporter</div>
            </div>
            <div className="Muted" style={{ marginBottom: 10 }}>
              Signale un report ultérieur (sans date prévue). L’équipe te recontactera pour replanifier.
            </div>
            <div className="Badges" style={{ justifyContent: 'flex-start' }}>
              <button type="button" className="Button Button--ghost" disabled={saving} onClick={() => doAction('postpone')}>
                {saving ? 'Enregistrement…' : 'Reporter sans date'}
              </button>
            </div>
          </div>

          <div className="Card">
            <div className="Row" style={{ marginBottom: 10 }}>
              <div className="TitleMd">Annuler</div>
            </div>
            <div className="Badges" style={{ justifyContent: 'flex-start' }}>
              <button
                type="button"
                className="Button Button--danger"
                disabled={saving}
                onClick={() => {
                  if (!window.confirm('Annuler ce rendez-vous ?')) return;
                  doAction('cancel');
                }}
              >
                <span className="ButtonInline">
                  <IconTrash /> Annuler le rendez-vous
                </span>
              </button>
            </div>
          </div>

          {result ? (
            <div className={result.ok ? 'Notice' : 'Error'} role={result.ok ? 'status' : 'alert'} style={{ marginTop: 14 }}>
              <span className="ButtonInline" style={{ gap: 10 }}>
                {result.ok ? <IconCheck /> : <IconAlertTriangle />}
                <span>{result.message}</span>
              </span>
            </div>
          ) : null}
        </>
      ) : null}
      </div>
  );

  if (embedded) return inner;
  return <div className="PageCenter">{inner}</div>;
}
